import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Brain, Clock, CheckCircle2, AlertTriangle } from "lucide-react";

const STATUS_TONE = {
  EARLY_EVIDENCE: "text-signal-yellow border-signal-yellow/40",
  GROWING_EVIDENCE: "text-[#0A84FF] border-[#0A84FF]/40",
  MEANINGFUL_SAMPLE: "text-signal-green border-signal-green/40",
  AUTONOMY_EVALUATION_ELIGIBLE: "text-signal-green border-signal-green/40",
};

function skipReasonLabel(reason) {
  if (!reason) return "";
  return {
    NO_ACTIVE_BROKER: "No Active Broker",
    NO_ANALYST_CONFIG: "Analyst Config Missing",
    NO_ACTIVE_SCANNER_POLICY: "No Active Scanner Policy",
    AI_BUDGET_LIMIT: "AI Budget Limit Reached",
    WORKSPACE_DISABLED: "Workspace Disabled",
    MARKET_DATA_UNAVAILABLE: "Market Data Unavailable",
    BROKER_STATE_INCONSISTENT: "Broker State Inconsistent",
    CONFIG_INVALID: "Configuration Invalid",
    UNKNOWN_PREREQUISITE: "Unknown Prerequisite",
    BUDGET_LIMIT_REACHED: "AI Budget Limit Reached",
  }[reason] || reason.replace(/_/g, " ");
}

function workspaceChipTone(status) {
  return {
    SUCCESS: "text-signal-green border-signal-green/40",
    RUNNING: "text-[#0A84FF] border-[#0A84FF]/40",
    PENDING: "text-[#0A84FF] border-[#0A84FF]/40",
    SCHEDULED: "text-muted-foreground border-border/60",
    SKIPPED: "text-signal-yellow border-signal-yellow/40",
    MISSED: "text-orange-500 border-orange-500/40",
    FAILED: "text-red-500 border-red-500/40",
  }[status] || "text-muted-foreground border-border/60";
}

function fmtET(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("en-US", { timeZone: "America/New_York",
      hour: "2-digit", minute: "2-digit", hour12: false }) + " ET";
  } catch { return iso; }
}

function fmtDay(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US",
      { timeZone: "America/New_York", month: "short", day: "numeric" });
  } catch { return iso; }
}

export default function EvidenceCard() {
  const [data, setData] = useState(null);
  const [hb, setHb] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/ai-quality/evidence-status")
      .then(r => { if (alive) setData(r.data); })
      .catch(() => {});
    api.get("/ai-quality/scheduler-heartbeat")
      .then(r => { if (alive) setHb(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!data) return null;
  const ev = data.evidence || {};
  const t = data.targets || {};
  const tone = STATUS_TONE[data.status] || "text-muted-foreground border-border";
  const sched = data.scheduler || {};
  const lastRes = data.last_research_scan;
  const lastFail = lastRes && lastRes.status === "FAILED";

  // Build a time-aware view over today's PREMARKET / MIDDAY / EOD slots
  // using the heartbeat payload (workspace_status included).
  const nowMs = Date.now();
  const slotsToday = (hb?.todays_slots || []);
  const findSlot = (session) => slotsToday.find(s => s.session === session);
  const buildRow = (session, nextIso, label) => {
    const slot = findSlot(session);
    if (slot) {
      const dt = slot.scheduled_for ? new Date(slot.scheduled_for) : null;
      const past = dt ? dt.getTime() < nowMs : false;
      return { session, label, dt, past, slot };
    }
    // No claim yet → treat as upcoming.
    return { session, label, dt: nextIso ? new Date(nextIso) : null,
              past: false, slot: null };
  };
  const rows = [
    buildRow("PREMARKET", sched.next_premarket_scan_et, "PRE-MARKET"),
    buildRow("MIDDAY", sched.next_midday_scan_et, "MIDDAY"),
    buildRow("AFTER_CLOSE_OUTCOME_REFRESH", sched.next_outcome_refresh_et, "EOD REFRESH"),
  ];

  const Progress = ({ label, cur, target, tid }) => {
    const pct = target > 0 ? Math.min(100, Math.round((cur / target) * 100)) : 0;
    const done = cur >= target;
    return (
      <div className="flex items-center justify-between text-[11px] mono" data-testid={tid}>
        <span className="text-muted-foreground">{label}</span>
        <span className={done ? "text-signal-green" : ""}>{cur} / {target}</span>
      </div>
    );
  };

  return (
    <Link to="/ai-quality" className="block no-underline" data-testid="evidence-card">
      <div className={`grid-cell !p-3 border-l-2 ${tone.split(" ").pop()} hover:bg-white/[0.02] transition`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 label-cap">
            <Brain size={12}/> AI EVIDENCE
          </div>
          <div className="flex items-center gap-2">
            <span className={`chip !text-[9px] ${tone}`} data-testid="evidence-status-chip">
              {(data.status || "").replace(/_/g, " ")}
            </span>
            {sched.scheduler_on
              ? <span className="chip !text-[9px] text-signal-green border-signal-green/40">SCHED ON</span>
              : <span className="chip !text-[9px] text-muted-foreground">SCHED OFF</span>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-2">
          <Progress label="Recommendations" cur={ev.snapshots_total || 0}
                    target={t.recommendations || 0} tid="ev-progress-recs"/>
          <Progress label="Actionable" cur={ev.actionable || 0}
                    target={t.actionable || 0} tid="ev-progress-actionable"/>
          <Progress label="Shadow Trades" cur={ev.shadow_trades || 0}
                    target={t.shadow_trades || 0} tid="ev-progress-shadow"/>
          <Progress label="T+20 Matured" cur={ev.matured?.T20D || 0}
                    target={t.matured_t20 || 0} tid="ev-progress-matured"/>
          <Progress label="Independent" cur={ev.independent_observations || 0}
                    target={t.recommendations || 0} tid="ev-progress-independent"/>
          <Progress label="Sectors" cur={ev.sectors_represented || 0}
                    target={t.sectors_represented || 0} tid="ev-progress-sectors"/>
        </div>

        <div className="border-t border-border/40 pt-2 mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-[10px] mono"
              data-testid="evidence-slot-rows">
          {rows.map((r) => {
            const dispatcher = r.slot?.dispatcher_status || r.slot?.status;
            const workspace = r.slot?.workspace_status;
            const reason = r.slot?.workspace_reason;
            const recs = r.slot?.workspace_recommendations;
            const chipTone = workspaceChipTone(workspace);
            return (
              <div key={r.session} className="flex items-center gap-1"
                    data-testid={`evidence-slot-${r.session}`}>
                <Clock size={10} className="flex-none"/>
                <span className="text-muted-foreground text-[9px] uppercase tracking-widest w-[76px]">
                  {r.past ? "Last" : "Next"} {r.label}
                </span>
                <span className="text-foreground">{fmtET(r.dt?.toISOString())}</span>
                {workspace && (
                  <span className={`ml-auto chip !text-[9px] ${chipTone}`}
                        title={`Dispatcher: ${dispatcher || "—"}`}>
                    {workspace}{recs ? ` · ${recs} recs` : ""}
                  </span>
                )}
                {!workspace && r.past && (
                  <span className="ml-auto text-muted-foreground">—</span>
                )}
              </div>
            );
          })}
          <div className="flex items-center gap-1 text-muted-foreground">
            <Clock size={10}/> Next T+20
            <span className="text-foreground ml-auto">{fmtDay(data.next_t20_maturity_date)}</span>
          </div>
        </div>

        <div className="border-t border-border/40 pt-2 mt-2 flex items-center justify-between text-[10px] mono">
          <div className="flex items-center gap-2 text-muted-foreground">
            {lastFail
              ? <><AlertTriangle size={10} className="text-signal-yellow"/>Last scan FAIL</>
              : lastRes
                ? lastRes.status === "SKIPPED"
                  ? <><AlertTriangle size={10} className="text-signal-yellow"/>Last scan SKIPPED · {skipReasonLabel(lastRes.skip_reason)}</>
                  : <><CheckCircle2 size={10} className="text-signal-green"/>Last scan {fmtET(lastRes.started_at)} · {lastRes.recommendations || 0} recs</>
                : <>No scans today</>}
          </div>
          <div className="text-muted-foreground">
            Today: {data.today?.recommendations || 0} recs · ${(data.today?.ai_cost_total_usd || 0).toFixed(2)}
          </div>
        </div>

        {lastFail && (
          <div className="mt-2 text-[10px] text-signal-yellow flex items-center gap-1" data-testid="evidence-card-attention">
            <AlertTriangle size={10}/> RESEARCH SCAN ATTENTION REQUIRED — click to view diagnostic
          </div>
        )}
      </div>
    </Link>
  );
}
